import React from 'react';
import { formatCurrency } from '../../../lib/utils';
import { Input } from '../../../components/ui/input';
import { Button } from '../../../components/ui/button';
import { Label } from '../../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Plus, Trash2 } from 'lucide-react';

const ITEM_CATEGORIES = ['Consultation', 'Lab Test', 'Imaging', 'Pharmacy', 'Room Charges', 'Procedure', 'Other'];

export const CreateBillDialog = ({
  patients, dialogOpen, setDialogOpen, patientIdFromUrl, formData, setFormData, addItem, removeItem, updateItem, handleSubmit, totals, navigate
}) => (
  <Dialog open={dialogOpen} onOpenChange={(open) => {
    setDialogOpen(open);
    if (!open && patientIdFromUrl) {
      navigate(`/patients/${patientIdFromUrl}`);
    }
  }}>
    <DialogTrigger asChild>
      <Button className="bg-red-500 hover:bg-red-600 rounded-full" data-testid="create-bill-btn">
        <Plus className="w-4 h-4 mr-2" />
        Create Bill
      </Button>
    </DialogTrigger>
    <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="font-[Manrope]">Create New Bill</DialogTitle>
      </DialogHeader>
      <form onSubmit={handleSubmit} className="space-y-4 mt-4">
        <div>
          <Label>Patient *</Label>
          <Select value={formData.patient_id} onValueChange={(v) => setFormData({...formData, patient_id: v})}>
            <SelectTrigger data-testid="select-patient">
              <SelectValue placeholder="Select patient" />
            </SelectTrigger>
            <SelectContent>
              {patients.map(p => (
                <SelectItem key={p.id} value={p.id}>{p.full_name} ({p.patient_id})</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <Label>Bill Items</Label>
            <Button type="button" variant="outline" size="sm" onClick={addItem}>
              <Plus className="w-3 h-3 mr-1" /> Add Item
            </Button>
          </div>
          <div className="space-y-3">
            {formData.items.map((item, index) => (
              <div key={item._key} className="p-3 bg-slate-50 rounded-lg space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Item {index + 1}</span>
                  {formData.items.length > 1 && (
                    <Button type="button" variant="ghost" size="sm" onClick={() => removeItem(index)}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  )}
                </div>
                <div className="grid grid-cols-4 gap-2">
                  <Input
                    placeholder="Description"
                    value={item.description}
                    onChange={(e) => updateItem(index, 'description', e.target.value)}
                    className="col-span-2"
                    data-testid={`item-desc-${index}`}
                  />
                  <Select value={item.category} onValueChange={(v) => updateItem(index, 'category', v)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Category" />
                    </SelectTrigger>
                    <SelectContent>
                      {ITEM_CATEGORIES.map(cat => (
                        <SelectItem key={cat} value={cat.toLowerCase()}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    type="number"
                    placeholder="Qty"
                    min="1"
                    value={item.quantity}
                    onChange={(e) => updateItem(index, 'quantity', parseInt(e.target.value) || 1)}
                  />
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <Input
                    type="number"
                    placeholder="Unit Price"
                    min="0"
                    step="0.01"
                    value={item.unit_price}
                    onChange={(e) => updateItem(index, 'unit_price', parseFloat(e.target.value) || 0)}
                  />
                  <div className="col-span-2 flex items-center justify-end">
                    <span className="text-sm font-medium">Total: {formatCurrency(item.total)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Discount (%)</Label>
            <Input
              type="number"
              min="0"
              max="100"
              value={formData.discount_percent}
              onChange={(e) => setFormData({...formData, discount_percent: parseFloat(e.target.value) || 0})}
              data-testid="input-discount"
            />
          </div>
          <div className="flex flex-col justify-end text-right">
            <p className="text-sm text-slate-500">Subtotal: {formatCurrency(totals.subtotal)}</p>
            <p className="text-sm text-slate-500">Discount: -{formatCurrency(totals.discount)}</p>
            <p className="text-lg font-semibold">Total: {formatCurrency(totals.total)}</p>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-4">
          <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button type="submit" className="bg-red-500 hover:bg-red-600" data-testid="submit-bill">Create Bill</Button>
        </div>
      </form>
    </DialogContent>
  </Dialog>
);
